import React, { MouseEvent, ReactElement, useCallback } from "react";

import Paragraph, { ParagraphProps } from "antd/es/typography/Paragraph";
import cn from "classnames";
import { useIntl } from "react-intl";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, ButtonProps } from "@mapmycustomers/ui";

import styles from "./LongContentParagraph.module.scss";

export type LongContentParagraphMode =
  | "clamped" // clamp to `rows`, no way to reveal the rest
  | "expandable" // clamp to `rows`, offer Expand / Collapse
  | "full"; // render everything; the caller's container owns overflow

export interface LongContentParagraphProps extends Omit<ParagraphProps, "className" | "ellipsis"> {
  className?: string;
  mode?: LongContentParagraphMode;
  // no margin is useful when this component is used for a list of tags or other things which do
  // not require a margin after it
  noMargin?: boolean;
  paragraphClassName?: string;
  renderActionButton?: (expanded: boolean, props: ButtonProps) => ReactElement<ButtonProps>;
  rows?: number; // ignored when mode is "full"
  showCollapseButtonInline?: boolean;
}

const LongContentParagraph = ({
  children,
  className,
  mode = "expandable",
  noMargin,
  paragraphClassName,
  renderActionButton,
  rows = 3,
  showCollapseButtonInline = false,
  ...props
}: LongContentParagraphProps) => {
  const intl = useIntl();
  const expandable = mode === "expandable";

  // Changing key toggles Paragraph node recreation and it's collapsed by default
  // So calling toggleKey collapses the paragraph
  // This is definitely a hacky way, but AntD doesn't support collapsing yet.
  const [key, , , toggleKey] = useBoolean();
  const [expanded, expand, collapse] = useBoolean();
  const handleExpand = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      expand();
    },
    [expand]
  );
  const handleCollapse = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      collapse();
      toggleKey();
    },
    [collapse, toggleKey]
  );

  const collapseLabel = expanded
    ? intl.formatMessage({
        id: "longContentParagraph.collapse",
        defaultMessage: "Collapse",
        description: "Collapse action in the Long Content Paragraph component",
      })
    : "";

  return (
    <div className={cn(styles.container, { [styles.noMargin]: noMargin }, className)}>
      <Paragraph
        className={cn(
          styles.paragraph,
          { [styles.paragraphInline]: showCollapseButtonInline && expanded },
          paragraphClassName
        )}
        ellipsis={
          mode === "full"
            ? false
            : {
                expandable,
                onExpand: expandable ? handleExpand : undefined,
                rows,
                symbol: expandable
                  ? (renderActionButton?.(false, {}) ??
                    intl.formatMessage({
                      id: "longContentParagraph.expand",
                      defaultMessage: "Expand",
                      description: "Expand action in the Long Content Paragraph component",
                    }))
                  : undefined,
              }
        }
        key={key ? 1 : 0}
        {...props}
      >
        {children}
      </Paragraph>
      {expanded &&
        (renderActionButton?.(true, { onClick: handleCollapse }) ?? (
          <Button
            aria-label={collapseLabel}
            className={styles.collapseButton}
            onClick={handleCollapse}
            type="link"
          >
            {collapseLabel}
          </Button>
        ))}
    </div>
  );
};

export default LongContentParagraph;
